import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { relativeDate } from '@/lib/taskRules'
import {
  CATEGORY_LABEL,
  KIND_LABEL,
  useNotifications,
  type AppNotification,
  type NotificationCategory,
} from '@/hooks/useNotifications'

const ORDER: NotificationCategory[] = ['attention', 'mine', 'available']

function Row({
  item,
  unread,
  onSelect,
}: {
  item: AppNotification
  unread: boolean
  onSelect: () => void
}) {
  return (
    <Link
      to={item.to}
      onClick={onSelect}
      className="flex gap-2 rounded-md px-2 py-2 text-left outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        aria-hidden="true"
        className={
          unread
            ? 'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground'
            : 'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-transparent'
        }
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm leading-snug">{item.title}</span>
        <span className="mt-1 flex items-center gap-1.5">
          {/* Hue only on human-decision kinds; pipeline events stay grey. */}
          <Badge variant={item.variant} className="px-1.5 py-0 text-[10px] font-medium">
            {KIND_LABEL[item.kind]}
          </Badge>
          <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground">
            {item.studyUid}
          </span>
        </span>
      </span>
      {item.at && (
        <span className="shrink-0 text-[11px] text-muted-foreground">{relativeDate(item.at)}</span>
      )}
    </Link>
  )
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const { items, unreadCount, isUnread, markRead, markAllRead } = useNotifications()

  const label =
    unreadCount > 0
      ? `Notifications, ${unreadCount} unread`
      : 'Notifications, none unread'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" aria-label={label}>
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              aria-hidden="true"
              className="absolute -right-1 -top-1 h-4 min-w-4 justify-center px-1 text-[10px] font-medium"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        <Separator />

        {items.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">
            You're caught up — no new work, decisions or pipeline holds.
          </p>
        ) : (
          <ScrollArea className="max-h-80">
            <div className="p-1">
              {ORDER.map((category) => {
                const group = items.filter((item) => item.category === category)
                if (group.length === 0) return null
                return (
                  <section key={category} aria-label={CATEGORY_LABEL[category]}>
                    <h3 className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {CATEGORY_LABEL[category]}
                    </h3>
                    {group.map((item) => (
                      <Row
                        key={item.id}
                        item={item}
                        unread={isUnread(item.id)}
                        onSelect={() => {
                          markRead(item.id)
                          setOpen(false)
                        }}
                      />
                    ))}
                  </section>
                )
              })}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  )
}
